import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Stack, Tooltip, Typography } from '@mui/material';
import * as changesets from 'json-diff-ts';

import { sortCounterPartyFirst } from '../../../Draft/Component/Helper';
import { IObject } from '../../../interfaces/IObject';
import PopoverPopupState from '../../../RiverusUI/Components/Popups/PopoverPopupState';

interface Props {
  list: any[];
  id: string;
  title?: string;
  filters?: IObject;
}

const ContractingPartiesList: React.FC<Props> = ({
  list,
  id,
  title,
  filters,
}) => {
  const [isOverflowed, setIsOverflowed] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  const contracting_parties = useMemo(() => {
    if (list?.length && (list[0]?.name || list[0]?.party || list[0]?.value)) {
      return list;
    }

    const raw_content = list?.[0]?.raw_content || { contracting_parties: [] };
    const edited_content = list?.[0]?.edited_content || { update: [] };
    const diff = changesets.unflattenChanges(edited_content.update);
    const changeList = JSON.parse(
      JSON.stringify(
        changesets.applyChangeset(JSON.parse(JSON.stringify(raw_content)), diff)
      )
    );
    const selectedFilters: any = filters?.contracting_parties;
    let reOrderContractingParties = changeList?.contracting_parties || [];
    if (Array.isArray(selectedFilters) && selectedFilters.length) {
      // Select dropdown
      const selected = reOrderContractingParties?.filter((item: any) =>
        selectedFilters.includes(item?.id)
      );
      const notSelected = reOrderContractingParties?.filter(
        (item: any) => !selectedFilters.includes(item?.id)
      );
      reOrderContractingParties = [...selected, ...notSelected];
    } else if (typeof selectedFilters === 'string' && selectedFilters.trim()) {
      // Text search
      const search = selectedFilters?.toLowerCase();
      const selected = reOrderContractingParties.filter((item: any) =>
        item?.party?.toLowerCase().includes(search)
      );
      const notSelected = reOrderContractingParties.filter(
        (item: any) => !item?.party?.toLowerCase().includes(search)
      );
      reOrderContractingParties = [...selected, ...notSelected];
    }
    return reOrderContractingParties;
  }, [list, filters]);

  const sortedByParties = useMemo(() => {
    return sortCounterPartyFirst(contracting_parties);
  }, [contracting_parties]);

  const partyName = useMemo(() => {
    if (!sortedByParties?.length) return '';
    const first = sortedByParties[0];
    return (
      first?.name || first?.partyName || first?.party || first?.value || ''
    );
  }, [sortedByParties]);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsOverflowed(el.scrollWidth > el.clientWidth);
    }
  }, [textRef]);

  return (
    <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
      <Tooltip title={isOverflowed ? partyName : ''}>
        <Box
          ref={textRef}
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '140px',
          }}
        >
          {partyName}
        </Box>
      </Tooltip>
      {sortedByParties?.length > 1 && (
        <PopoverPopupState
          id={id}
          renderIcon={() => (
            <Typography
              sx={{ fontSize: '12px', color: 'riNeutralVariant.700' }}
            >
              {sortedByParties?.length - 1}+
            </Typography>
          )}
        >
          <Stack spacing={2} direction="column" width="100%">
            {title && (
              <Typography fontSize="15px" fontWeight={600}>
                {title}
              </Typography>
            )}
            {sortedByParties?.map((item: any, index: number) => (
              <Typography key={index} fontSize="14px">
                {index + 1}).{' '}
                {item?.name || item?.partyName || item?.party || item?.value}
              </Typography>
            ))}
          </Stack>
        </PopoverPopupState>
      )}
    </Stack>
  );
};

export default ContractingPartiesList;
